import React, { useEffect, useRef, useState } from 'react';
import * as $3Dmol from '3dmol';
import { ComplexReference, coordinateUrl } from '../../data/ppiStructuralEvidence';

/** Uses the same 3Dmol engine as StructureViewer; no docking or energetic calculations. */
export default function ComplexStructureWindow({ reference, allowComponentViews = false }: { reference: ComplexReference; allowComponentViews?: boolean }) {
  const container = useRef<HTMLDivElement>(null);
  const targetContainer = useRef<HTMLDivElement>(null);
  const partnerContainer = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<$3Dmol.GLViewer | null>(null);
  const savedView = useRef<number[] | null>(null);
  const [showPartners, setShowPartners] = useState(false);
  const [selectedResidue, setSelectedResidue] = useState('');
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [retry, setRetry] = useState(0);
  const [surface, setSurface] = useState(false);
  const [surfaceBusy, setSurfaceBusy] = useState(false);
  const [surfaceError, setSurfaceError] = useState(false);

  useEffect(() => {
    if (!container.current) return;
    const element = container.current;
    const controller = new AbortController();
    let disposed = false;
    let viewer: $3Dmol.GLViewer | null = null;
    const viewers: $3Dmol.GLViewer[] = [];
    const elements = [element, ...(showPartners ? [targetContainer.current, partnerContainer.current].filter((e): e is HTMLDivElement => !!e) : [])];
    let observer: ResizeObserver | undefined;
    setStatus('loading');
    setSurface(false);
    setSurfaceBusy(false);
    setSurfaceError(false);
    setSelectedResidue('');
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    (async () => {
      try {
        const response = await fetch(coordinateUrl(reference), { signal: controller.signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Coordinate file unavailable');
        const cif = await response.text();
        if (disposed) return;
        const target = { lchain: reference.targetChain };
        const partner = { lchain: reference.partnerChain };
        for (const pane of elements) {
          const instance = $3Dmol.createViewer(pane, { backgroundColor: '#f8fafc', antialias: true });
          viewers.push(instance);
          instance.addModel(cif, 'cif', { doAssembly: false });
        }
        viewer = viewers[0];
        viewerRef.current = viewer;
        if (!viewer.selectedAtoms(target).length || !viewer.selectedAtoms(partner).length) {
          throw new Error('Verified chains not present');
        }
        const vicinity = { ...target, within: { distance: 5, sel: partner }, byres: true };
        if (!viewer.selectedAtoms(vicinity).length) throw new Error('Interface not present');
        // Hide water, additives, and other crystal copies; show the verified assembly-1 pair.
        const stylePane = (pane: $3Dmol.GLViewer, index: number, selected?: $3Dmol.AtomSelectionSpec) => {
          pane.setStyle({}, {});
          if (index !== 2) {
            pane.setStyle(target, { cartoon: { color: '#0f766e' } });
            pane.addStyle(vicinity, { stick: { color: '#8b5cf6', radius: 0.12 } });
          }
          if (index !== 1) pane.setStyle(partner, { stick: { color: '#e89124', radius: 0.19 }, cartoon: { color: '#e89124' } });
          if (selected) {
            const visible = index === 0 || (index === 1 && selected.lchain === reference.targetChain) || (index === 2 && selected.lchain === reference.partnerChain);
            if (visible) pane.addStyle(selected, { stick: { color: '#db2777', radius: 0.25 }, sphere: { color: '#db2777', scale: 0.22 } });
          }
        };
        viewers.forEach((pane, index) => {
          stylePane(pane, index);
          pane.zoomTo({ or: [target, partner] });
          if (savedView.current) pane.setView(savedView.current);
          pane.setClickable(index === 1 ? target : index === 2 ? partner : { or: [target, partner] }, true, (atom: $3Dmol.AtomSpec) => {
            if (disposed) return;
            const selection = { lchain: atom.lchain, resi: atom.resi };
            setSelectedResidue(`${atom.resn} ${atom.resi}${atom.icode || ''} · ${atom.lchain === reference.targetChain ? reference.target : reference.partner} · chain ${atom.lchain}`);
            viewers.forEach((other, otherIndex) => { stylePane(other, otherIndex, selection); other.render(); });
          });
          pane.render();
        });
        // Every pane retains the original complex coordinates and shares its camera matrix.
        viewers.forEach(pane => viewers.filter(other => other !== pane).forEach(other => pane.linkViewer(other)));
        observer = new ResizeObserver(() => { if (!disposed) viewers.forEach(pane => pane.resize()); });
        elements.forEach(pane => observer?.observe(pane));
        setStatus('ready');
      } catch {
        if (!disposed) {
          viewers.forEach(pane => pane.clear());
          setStatus('error');
        }
      } finally { window.clearTimeout(timeout); }
    })();
    return () => {
      disposed = true;
      controller.abort();
      window.clearTimeout(timeout);
      observer?.disconnect();
      if (viewer) savedView.current = viewer.getView();
      viewers.forEach(pane => { pane.spin(false); pane.clear(); });
      // Release the canvas GPU context when the pane closes or its reference changes.
      elements.forEach(pane => {
        pane.querySelectorAll('canvas').forEach(canvas => {
          const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
          gl?.getExtension('WEBGL_lose_context')?.loseContext();
        });
        pane.replaceChildren();
      });
      viewerRef.current = null;
    };
  }, [reference, retry, showPartners]);

  const focus = (whole: boolean) => {
    const viewer = viewerRef.current;
    if (!viewer || status !== 'ready') return;
    viewer.zoomTo(whole ? { or: [{ lchain: reference.targetChain }, { lchain: reference.partnerChain }] } : { lchain: reference.partnerChain });
    if (!whole) viewer.zoom(0.65);
    viewer.render();
  };
  const toggleSurface = async () => {
    const viewer = viewerRef.current;
    if (!viewer || status !== 'ready' || surfaceBusy) return;
    setSurfaceBusy(true);
    setSurfaceError(false);
    try {
      if (surface) viewer.removeAllSurfaces();
      else await viewer.addSurface($3Dmol.SurfaceType.VDW, { opacity: 0.35, color: '#0f766e' }, { lchain: reference.targetChain });
      if (viewerRef.current !== viewer) return;
      setSurface(!surface);
      viewer.render();
    } catch { if (viewerRef.current === viewer) setSurfaceError(true); }
    finally { if (viewerRef.current === viewer) setSurfaceBusy(false); }
  };

  return <section className="rounded-xl border border-slate-200 overflow-hidden bg-white min-w-0">
    <header className="p-3 border-b border-slate-100">
      <div className="flex justify-between gap-3 text-xs text-slate-500">
        <span>Experimental · X-ray · {reference.resolution} Å</span>
        <a href={`https://www.rcsb.org/structure/${reference.pdbId}`} target="_blank" rel="noopener noreferrer" className="text-teal-700 underline">PDB {reference.pdbId} ↗</a>
      </div>
      <h3 className="font-semibold text-sm text-slate-800 mt-2">{reference.target} + {reference.partner}</h3>
      <p className="text-xs text-slate-500 mt-1">{reference.kind === 'protein-peptide' ? 'Protein–partner peptide complex' : 'Protein–compound co-crystal'} · selected chains from assembly 1</p>
    </header>
    <div className="relative">
      <div className={`grid ${showPartners ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1'}`}>
        <div><p className="text-xs text-center text-slate-600 py-2 bg-slate-100">Complete selected complex</p><div ref={container} aria-label={`Interactive 3D structure ${reference.pdbId}`} className="relative" style={{ height: 300 }} /></div>
        {showPartners && <>
          <div className="border-l"><p className="text-xs text-center text-teal-700 py-2 bg-teal-50">{reference.target}</p><div ref={targetContainer} aria-label="Synchronized target component" className="relative" style={{ height: 300 }} /></div>
          <div className="border-l"><p className="text-xs text-center text-amber-700 py-2 bg-amber-50">{reference.partner}</p><div ref={partnerContainer} aria-label="Synchronized partner component" className="relative" style={{ height: 300 }} /></div>
        </>}
      </div>
      {status === 'loading' && <div role="status" className="absolute inset-0 grid place-items-center bg-slate-50 text-sm text-slate-500">Loading {reference.pdbId}…</div>}
      {status === 'error' && <div role="alert" className="absolute inset-0 flex flex-col gap-3 items-center justify-center bg-slate-50 text-sm text-slate-600 p-4 text-center">
        <p>3D structure unavailable. No substitute or predicted pose has been shown.</p>
        <button className="text-teal-700 underline" onClick={() => setRetry(n => n + 1)}>Retry {reference.pdbId}</button>
      </div>}
    </div>
    <div className="px-3 py-2 flex flex-wrap gap-3 border-t text-xs">
      <button disabled={status !== 'ready'} onClick={() => focus(true)} className="text-teal-700 disabled:opacity-40">Whole complex</button>
      <button disabled={status !== 'ready'} onClick={() => focus(false)} className="text-teal-700 disabled:opacity-40">Focus interface</button>
      <button disabled={status !== 'ready' || surfaceBusy} onClick={toggleSurface} aria-pressed={surface} className="text-teal-700 disabled:opacity-40">{surfaceBusy ? 'Loading surface…' : `${surface ? 'Hide' : 'Show'} target surface`}</button>
      {allowComponentViews && <button onClick={() => setShowPartners(value => !value)} aria-pressed={showPartners} className="text-teal-700">{showPartners ? 'Hide component panes' : 'Show synchronized partner panes'}</button>}
    </div>
    <div className="px-3 pb-3 text-xs text-slate-500 space-y-2">
      {surfaceError && <p role="status">Surface unavailable; the experimental coordinates remain visible.</p>}
      {selectedResidue && <p role="status" className="text-pink-700">Selected: {selectedResidue}. Highlighted in every pane where this component is present.</p>}
      <p><span className="text-teal-700">■ Target ({reference.targetChain})</span> · <span className="text-amber-700">■ Partner ({reference.partnerChain})</span> · <span className="text-violet-600">■ Contact vicinity</span></p>
      <p>Drag to rotate · scroll to zoom · click a residue to highlight it. {showPartners ? 'The three panes share rotation and zoom; component positions are preserved. ' : ''}Purple: target residues within 5 Å of the partner; a geometric aid, not binding energy.</p>
      <p>{reference.caveat}</p>
    </div>
  </section>;
}
